// plugins/googleTagManager.ts
import _Vue from 'vue';
import { ContactType } from '@/views/Contact/components/ContactForm/ContactForm.models';
import VueRouter from 'vue-router';
import isInBrowserContext from '@/utils/helpers/isInBrowserContext';
import { Store } from 'vuex';
import { RootState } from '@/store';

interface DataLayerObject extends Record<string, unknown> {
  event?: string;
  code?: string;
}

export interface GoogleTagManagerOptions {
  router: VueRouter;
  store: Store<RootState>;
  debug?: boolean;
}

class GtmPlugin {
  dataLayer: DataLayerObject[];
  router: VueRouter;
  store: Store<RootState>;
  debug: boolean;
  isBrowser: boolean;

  constructor(options: GoogleTagManagerOptions) {
    this.dataLayer = window.dataLayer = window.dataLayer ?? [];
    this.router = options.router;
    this.store = options.store;
    this.debug = options.debug ?? false;
    this.isBrowser = isInBrowserContext();
    this.initRouterGuard();
  }

  initRouterGuard(): void {
    this.router.afterEach(async to => {
      // Dispatch vue event using meta gtm value if defined otherwise fallback to route name
      const name: string = to.meta && typeof to.meta.gtm === 'string' && !!to.meta.gtm ? to.meta.gtm : to.name;
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      const baseUrl: string = this.router.options.base ?? '';
      let fullUrl: string = baseUrl;
      if (!fullUrl.endsWith('/')) {
        fullUrl += '/';
      }
      fullUrl += to.fullPath.startsWith('/') ? to.fullPath.substr(1) : to.fullPath;

      this.trackView(name, fullUrl);
    });
  }

  trackView(name: string, url: string) {
    const eventObject: DataLayerObject = {
      event: 'msgh_content_view',
      // eslint-disable-next-line @typescript-eslint/camelcase
      content_name: url,
      // eslint-disable-next-line @typescript-eslint/camelcase
      content_view_name: name
    };

    this.pushToDatalayer(eventObject);
  }

  trackEvent(event: string, additionalParams: Record<string, unknown> = {}, noPrefix = false): void {
    const eventObject: DataLayerObject = {
      event: noPrefix ? event : `msgh_${event}`,
      ...additionalParams
    };
    this.pushToDatalayer(eventObject);
  }

  trackSubmitMail(): void {
    this.trackEvent('send_configuration_email');
  }

  trackLoadByCode(code?: string): void {
    let additionalParams: {
      // eslint-disable-next-line @typescript-eslint/camelcase
      content_code?: string;
    };
    if (code) {
      additionalParams = {
        // eslint-disable-next-line @typescript-eslint/camelcase
        content_code: code
      };
    } else {
      additionalParams = {};
    }
    this.trackEvent('load_configuration_code', additionalParams);
  }

  trackConfigurationPrint(): void {
    this.trackEvent('hausplaung_drucken');
  }

  trackConfigurationCreated(): void {
    this.trackEvent('created_configuration');
  }

  trackConfigurationStarted(preset: string): void {
    this.trackEvent(preset, {}, true);
  }

  trackAppointment(type: ContactType, estate: string): void {
    this.trackEvent(GtmPlugin.getAppointmentKey(type, estate));
  }

  private pushToDatalayer(event: DataLayerObject) {
    // do not track anything if not in browser
    if (!this.isBrowser) {
      return;
    }

    // add configuration code to event if possible
    if (!event.code) {
      const code = this.store.getters['configuration/currentCode'];
      if (code) {
        event.code = code;
      }
    }

    if (this.debug) {
      console.log(`[gtm] Event ${event.event} pushed`);
    }

    // define structure for view event
    this.dataLayer.push(event);
  }

  private static getAppointmentKey(type: ContactType, estate: string): string {
    const keyArray: string[] = [];

    switch (type) {
      case ContactType.Appointment:
        keyArray.push('beratungstermin');
        break;
      case ContactType.Offer:
        keyArray.push('angebotsanfrage');
        break;
      default:
        console.error('impossible contact type');
    }

    keyArray.push(estate.toLowerCase());

    keyArray.push('estate');

    return keyArray.join('_');
  }
}

export default {
  install: (Vue: typeof _Vue, options: GoogleTagManagerOptions) => {
    Vue.prototype.$gtm = new GtmPlugin(options);
  }
};

declare module 'vue/types/vue' {
  export interface Vue {
    /**
     * The Vue GTM Plugin instance.
     */
    $gtm: GtmPlugin;
  }
}
